import React from 'react'
import { Button, Icon } from 'react-bulma-components'
import { CopyButton } from '../clipboard/copy-button'
import { PasteButton } from '../clipboard/paste-button'
import { ClipboardBaseButton } from '../clipboard/clipboard-base-button'
import { RemoveIcon } from '../icons'

export interface DayCellActionsProps {
  onCopy: () => void
  onPaste: () => void
  onRemove?: () => void
  copyDisabled: boolean
  pasteDisabled: boolean
  removeDisabled?: boolean
  /** Prefix for data-test: Copy → day-copy-action-${prefix}, Paste → day-paste-action-${prefix}. If undefined, uses day-copy-action / day-paste-action (no suffix). */
  dataTestPrefix?: string
  /** data-test for the Remove button when onRemove is provided. Defaults to day-remove-action-${dataTestPrefix} or remove-action when no prefix. */
  removeDataTest?: string
}

/**
 * Shared group of Copy / Paste / optional Remove buttons for a day cell.
 * No clipboard or form logic: parent provides callbacks and disabled state.
 */
export const DayCellActions: React.FC<DayCellActionsProps> = ({
  onCopy,
  onPaste,
  onRemove,
  copyDisabled,
  pasteDisabled,
  removeDisabled = false,
  dataTestPrefix,
  removeDataTest,
}) => {
  const copyDataTest =
    dataTestPrefix !== undefined ? `day-copy-action-${dataTestPrefix}` : 'day-copy-action'
  const pasteDataTest =
    dataTestPrefix !== undefined ? `day-paste-action-${dataTestPrefix}` : 'day-paste-action'
  const defaultRemoveDataTest =
    dataTestPrefix !== undefined ? `day-remove-action-${dataTestPrefix}` : 'remove-action'
  const removeTest = removeDataTest ?? defaultRemoveDataTest

  return (
    <Button.Group hasAddons>
      <CopyButton
        size="extra-small"
        data-test={copyDataTest}
        disabled={copyDisabled}
        onClick={onCopy}
      />
      <PasteButton
        size="extra-small"
        data-test={pasteDataTest}
        disabled={pasteDisabled}
        onClick={onPaste}
      />
      {onRemove != null && (
        <ClipboardBaseButton
          size="extra-small"
          data-test={removeTest}
          disabled={removeDisabled}
          onClick={onRemove}
        >
          <Icon>
            <RemoveIcon />
          </Icon>
        </ClipboardBaseButton>
      )}
    </Button.Group>
  )
}
